import { skipToken, useInfiniteQuery } from '@tanstack/react-query';
import { getV1CompaniesCompanyIdVetaCenterTestTickets } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';

import { DEFAULT_API_PAGE_SIZE } from '~constants/consts';
import { VetaCenterTestTicket } from '~hooks/useVetaCenter/models';
import { VETA_CENTER_POLL_INTERVAL_IN_MS } from '~hooks/useVetaCenter/useVetaCenterPollWindow';
import connection from '~services/connectionModule';
import { extractPagination, Pagination } from '~services/pagination';

const VETA_CENTER_TEST_TICKETS_QUERY_KEY = 'vetaCenterTestTickets';

interface TestTicketsPage {
  pagination: Pagination;
  testTickets: VetaCenterTestTicket[];
}

export const getVetaCenterTestTicketsQueryKey = (companyId: string) => [
  VETA_CENTER_TEST_TICKETS_QUERY_KEY,
  companyId,
];

export default function useVetaCenterTestTicketsInfiniteQuery(
  companyId: string,
  projectLinkIds: string[],
  isPolling: boolean,
) {
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isError,
    isFetching,
    isFetchingNextPage,
    isLoading,
    refetch,
  } = useInfiniteQuery({
    getNextPageParam: (lastPage: TestTicketsPage) =>
      lastPage.pagination.after ? lastPage.pagination : undefined,
    initialPageParam: undefined as Pagination | undefined,
    queryFn: projectLinkIds.length
      ? async ({ pageParam, signal }): Promise<TestTicketsPage> => {
          try {
            const response = await getV1CompaniesCompanyIdVetaCenterTestTickets({
              path: { 'company-id': companyId },
              query: {
                'page[after]': pageParam?.after,
                'page[limit]': DEFAULT_API_PAGE_SIZE,
              },
              signal,
            });

            return {
              pagination: extractPagination(response),
              testTickets: response.data.data.map(VetaCenterTestTicket.parse),
            };
          } catch (error) {
            connection.handleRequestError(
              error,
              t('error_messages.veta_center.failed_to_fetch_test_tickets'),
            );
            throw error;
          }
        }
      : skipToken,
    queryKey: [...getVetaCenterTestTicketsQueryKey(companyId), projectLinkIds],
    refetchInterval: ({ state }) =>
      isPolling && state.status !== 'error' ? VETA_CENTER_POLL_INTERVAL_IN_MS : false,
    retry: false,
  });

  return {
    fetchNextPage,
    hasNextPage,
    isError,
    isFetching,
    isFetchingNextPage,
    isLoading,
    refetch,
    testTickets: data?.pages.flatMap((page) => page.testTickets) ?? [],
  };
}
